import {useFormatCurrency} from '@aboutyou/i18n';
import {COLORS} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useCallback, useEffect, useState} from 'react';

const SLOT_NAME = 'slot_replace_basket_total';
const conversionRateBGN = 1955830;

export interface CheckoutUpdateEvent extends CustomEvent {
  detail: {
    orderState: {
      basket: {
        total: {
          costCapture: number;
        };
      };
    };
  };
}

interface Props {
  amount: number | undefined;
  currencyCode: string;
  formatPrice: (amount: number, currencyCode: string) => string;
}

enum CONVERSION_DIRECTION {
  TO_EUR,
  TO_LOCAL_CURRENCY,
}

const calculatePrice = (
  conversionDirection: CONVERSION_DIRECTION,
  amount: number,
) => {
  switch (conversionDirection) {
    case CONVERSION_DIRECTION.TO_EUR:
      return Math.round((amount / conversionRateBGN) * 1000000);
    case CONVERSION_DIRECTION.TO_LOCAL_CURRENCY:
      return Math.round((amount * conversionRateBGN) / 1000000);
  }
};

const getPriceForPhase = ({
  amount,
  phase1,
}: Props & {phase1: boolean}): number | null => {
  if (amount === undefined) {
    return null;
  }

  if (phase1) {
    return calculatePrice(CONVERSION_DIRECTION.TO_EUR, amount);
  } else {
    return calculatePrice(CONVERSION_DIRECTION.TO_LOCAL_CURRENCY, amount);
  }

  return null;
};

const getSecondaryPriceLabelForLocale = (props: Props): string => {
  const {currencyCode} = props;
  const phase1 = currencyCode !== 'EUR';
  const calculatedPrice = getPriceForPhase({...props, phase1});

  if (!calculatedPrice) {
    return '';
  }
  const returnCurrencyCode = phase1 ? 'EUR' : 'BGN';

  return ` (${props.formatPrice(calculatedPrice, returnCurrencyCode)}³)`;
};

export const TotalPriceWithSecondaryPriceLabelSlot: React.FC<{
  webComponentRef: any;
}> = ({webComponentRef}) => {
  const [secondaryPriceLabel, setSecondaryPriceLabel] = useState<
    string | undefined
  >();
  const formatPrice = useFormatCurrency();

  const updateSecondaryPriceLabel = useCallback(
    (event: CheckoutUpdateEvent) => {
      const currency = window.scayle?.config?.currency;
      const total = event.detail.orderState.basket.total.costCapture;
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setSecondaryPriceLabel(
        getSecondaryPriceLabelForLocale({
          amount: total,
          currencyCode: currency!,
          formatPrice,
        }),
      );
    },
    [formatPrice],
  );

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (webComponentRef?.current !== null) {
      webComponentRef.current.addEventListener(
        'update',
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        updateSecondaryPriceLabel,
      );
    }

    return () => {
      if (webComponentRef?.current !== null) {
        webComponentRef.current.removeEventListener(
          'update',
          updateSecondaryPriceLabel,
        );
      }
    };
  }, [updateSecondaryPriceLabel, webComponentRef]);

  if (!secondaryPriceLabel) {
    return null;
  }

  return <PriceWrapper slot={SLOT_NAME}>{secondaryPriceLabel}</PriceWrapper>;
};

const PriceWrapper = styled.span`
  color: ${COLORS['Text.Grey']};
  margin-left: 0.25rem;
`;
